import firebase from 'firebase';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import firebaseApp from '../../../../services/firebaseService';
import { EmployeeHelper, IUserSession } from '../../../employee';
import { IValueAddedTaxFormValues } from './valueAddedTaxFormValues';
import moment from 'moment';
import { IValueAddedTax } from './valueAddedTax';

export default class ValueAddedTaxModelHelper {
    public static readonly COLLECTION_NAME = 'value_added_tax';

    /*================================================================================================================
     *                                                  Converters
     * ==============================================================================================================*/

    private static converter : firebase.firestore.FirestoreDataConverter<IValueAddedTax> = {

        fromFirestore: (snapshot) => {
            return ValueAddedTaxModelHelper.fromFirestoreDocument(snapshot);
        },

        toFirestore: (data : IValueAddedTax) : firebase.firestore.DocumentData => {
            return ValueAddedTaxModelHelper.toFirestoreDocument(data);
        },
    };

    public static fromFirestoreDocument(snapshot : firebase.firestore.DocumentSnapshot | firebase.firestore.QueryDocumentSnapshot) : IValueAddedTax {
        const data = snapshot.data() as IValueAddedTax;
        if (!snapshot.exists) throw new Error(`Document does not exist! ${snapshot.id}`);
        
        return {
            ...data,
            ref: snapshot.ref,
        };
    }

    public static toFirestoreDocument(data : IValueAddedTax) {
        return ValueAddedTaxModelHelper.convertToObject(data);
    }

    public static convertToObject(model : IValueAddedTax) {
        const obj = {
            ...model,
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ref, ...rest } = obj;
        return rest;
    }

    /*================================================================================================================
     *                                                Handler Methods
     * ==============================================================================================================*/

    public static collection() {
        return firebaseApp
            .firestore()
            .collection(ValueAddedTaxModelHelper.COLLECTION_NAME)
            .withConverter(ValueAddedTaxModelHelper.converter);
    }

    public static async save(entry : IValueAddedTax) : Promise<void> {
        if (entry.ref) {
            ValueAddedTaxModelHelper
                .collection()
                .doc(entry.ref.id)
                .set(entry, { merge: true });

        } else {
            ValueAddedTaxModelHelper
                .collection()
                .add(entry);
        }
    }

    /*================================================================================================================
     *                                                  Form Values
     * ==============================================================================================================*/

    public static createFormValues = (existing ?: IValueAddedTax | null) : IValueAddedTaxFormValues => {
        if (existing) {
            return {
                guid: existing.guid,
                value: existing.value,
                startDate: moment(existing.startDate.toMillis()),
                endDate: existing.endDate ? moment(existing.endDate.toMillis()) : null,
                isActive: existing.isActive,
            };
        } else {
            return {
                guid: uuidv4(),
                value: 0,
                startDate: moment(),
                endDate: null,
                isActive: true,
            };
        }
    };

    public static createInactiveSave = (user : IUserSession, entry : IValueAddedTax) : IValueAddedTax => {        
        return {
            ...entry,
            updatedByEmployee: user.employee.EmployeeNumber ?? '',
            updatedByName: user.employee.Name,
            updatedBy: firebaseApp
                .firestore()
                .collection(EmployeeHelper.COLLECTION)
                .doc(user.firebaseUser.uid),
            updatedOn: firebase.firestore.Timestamp.now(),
            isActive: false,
        };
    };

    public static createSave = (user : IUserSession, formValues : IValueAddedTaxFormValues, existingEntry ?: IValueAddedTax | null) : IValueAddedTax => {       

        if (existingEntry) {
            return {
                ...existingEntry,
                value: formValues.value,
                startDate: firebase.firestore.Timestamp.fromMillis(formValues.startDate.valueOf()),
                endDate: formValues.endDate ? firebase.firestore.Timestamp.fromMillis(formValues.startDate.valueOf()) : null,
                updatedByEmployee: user.employee.EmployeeNumber ?? '',
                updatedByName: user.employee.Name,
                updatedBy: firebaseApp.firestore().collection(EmployeeHelper.COLLECTION).doc(user.firebaseUser.uid),
                updatedOn: firebase.firestore.Timestamp.now(),
                isActive: formValues.isActive,
            };

        } else {
            return {

                guid: formValues.guid,
                value: formValues.value,
                startDate: firebase.firestore.Timestamp.fromMillis(formValues.startDate.valueOf()),
                endDate: formValues.endDate ? firebase.firestore.Timestamp.fromMillis(formValues.endDate.valueOf()) : null,
                createdByEmployee: user.employee.EmployeeNumber ?? '',
                createdByName: user.employee.Name,
                createdOn: firebase.firestore.Timestamp.now(),
                createdBy: firebaseApp.firestore().collection(EmployeeHelper.COLLECTION).doc(user.firebaseUser.uid),
                updatedByEmployee: user.employee.EmployeeNumber ?? '',
                updatedByName: user.employee.Name,
                updatedBy: firebaseApp.firestore().collection(EmployeeHelper.COLLECTION).doc(user.firebaseUser.uid),
                updatedOn: firebase.firestore.Timestamp.now(),
                isActive: true,
            };
        }
    };

    /*================================================================================================================
     *                                                  Schemas
     * ==============================================================================================================*/

    public static formSchema = () : Yup.ObjectSchema<IValueAddedTaxFormValues> => Yup.object({
        guid: Yup.string().required('Required'),
        value: Yup.number()
            .typeError('Enter a valid number.')
            .required('Enter a valid number.'),
        startDate: Yup.mixed<moment.Moment>().required('Required'),
        endDate: Yup.mixed<moment.Moment>().nullable().defined(),
        isActive: Yup.boolean().required('Required'), 
    });
}
